import {
  Box,
  Button,
  Modal,
  TextareaAutosize,
  Typography,
} from "@material-ui/core";
import { useState } from "react";
import LinearProgress from "@material-ui/core/LinearProgress";
import { ModalStyles } from "../styles";
import { useFormik } from "formik";
import { Api } from "../lib/api";
import {
  CreateContentBodyParams,
  ImportByUrlQueryParams,
} from "@authory-packages/core";

type BulkImportPanelProps = {
  open: boolean;
  onClose: () => void;
  userSlug: string;
  websiteSlug?: string;
  onImportFinished: () => void;
};

function LinearProgressWithLabel(props: any) {
  return (
    <Box display="flex" alignItems="center">
      <Box width="100%" mr={1}>
        <LinearProgress variant="determinate" {...props} />
      </Box>
      <Box minWidth={35}>
        <Typography
          variant="body2"
          color="textSecondary"
        >{`${props.value.toFixed(2)}%`}</Typography>
      </Box>
    </Box>
  );
}

export const BulkImportPanel = ({
  open,
  onClose,
  userSlug,
  websiteSlug,
  onImportFinished,
}: BulkImportPanelProps) => {
  const modalStyles = ModalStyles();

  const [importInProgress, setImportInProgress] = useState(false);
  const [bulkImportSize, setBulkImportSize] = useState(0);
  const [bulkImportCompleted, setBulkImportCompleted] = useState(0);
  const [successImportCount, setSuccessImportCount] = useState(0);
  const [importDone, setImportDone] = useState(false);
  const [failReport, setFailReport] = useState("");
  const [currentUrl, setCurrentUrl] = useState("");

  const formik = useFormik({
    initialValues: {
      urls: "",
    },
    onSubmit: async (data) => {
      // Set import in progress (changes modal UI)
      setImportInProgress(true);

      // Grab and parse the urls
      const urlSplit = data.urls
        .split("\n")
        .map((u) => u.trim())
        .filter((u) => u.length > 0);

      // Set initial stats
      setBulkImportSize(urlSplit.length);
      setBulkImportCompleted(0);
      setSuccessImportCount(0);
      setFailReport("");
      setCurrentUrl("");

      // Impersonate the user we're importing for
      let response = await Api.impersonate(userSlug);
      let userToken = response.data.token;
      console.log(`Impersonate ${userSlug} token: `, userToken);

      let succ = 0;
      let failReport = "";

      for (let index = 0; index < urlSplit.length; index++) {
        const url = urlSplit[index];

        setCurrentUrl(url);

        try {
          const importURlParams = new ImportByUrlQueryParams();
          importURlParams.url = url;

          let findContentResponse = await Api.findContent(
            importURlParams,
            userToken
          );

          let createContentParams = new CreateContentBodyParams();

          if (websiteSlug) {
            createContentParams.source = websiteSlug;
          } else {
            // Create new source if not found
            if (!findContentResponse.data.source) {
              const createWebsiteData = {
                url: findContentResponse.data.url,
                parentCanonicalSlug: null,
                active: false,
              };
              const createWebsiteResponse = await Api.createSource(
                userSlug,
                createWebsiteData
              );

              createContentParams.source =
                createWebsiteResponse.data.canonicalSlug;
            } else {
              createContentParams.source = findContentResponse.data.source;
            }
          }

          createContentParams.url = findContentResponse.data.url;
          createContentParams.content = findContentResponse.data.content;
          createContentParams.type = findContentResponse.data.type;
          createContentParams.title = findContentResponse.data.title || "";
          createContentParams.date = findContentResponse.data.date;

          // Create new content
          await Api.createContent(createContentParams, userToken);

          succ++;
        } catch (e) {
          failReport += `${url} \n`;
        }

        setBulkImportCompleted(index + 1);
      }

      setSuccessImportCount(succ);
      setFailReport(failReport);
      setImportDone(true);
      onImportFinished();
    },
    enableReinitialize: true,
  });

  return (
    <Modal open={open} disableBackdropClick={true} onClose={onClose}>
      <div className={modalStyles.paper}>
        {importInProgress ? (
          <>
            <Box mb={1}>
              <Typography variant="h4" component="h4">
                Import {importDone ? "Done" : "in Progress"}
              </Typography>
            </Box>
            <Box minWidth={35}>
              <Typography variant="body2" color="textSecondary">
                {bulkImportCompleted}/{bulkImportSize}
              </Typography>
            </Box>
            {!importDone && (
              <Box minWidth={35}>
                <Typography variant="body2" color="textSecondary">
                  Processing {currentUrl}
                </Typography>
              </Box>
            )}
            <LinearProgressWithLabel
              value={
                bulkImportSize === 0
                  ? 0
                  : (bulkImportCompleted / bulkImportSize) * 100
              }
            />
            {importDone && (
              <>
                <Box minWidth={35} mb={4}>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                  >{`Successfully imported ${successImportCount} urls`}</Typography>
                </Box>
                {!!failReport.length && (
                  <Box minWidth={35}>
                    <Typography variant="h6" color="textSecondary">
                      Failed Urls:
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                    ></Typography>
                    <textarea
                      style={{
                        width: "100%",
                        minHeight: "180px",
                        overflow: "auto",
                        resize: "none",
                      }}
                    >
                      {failReport}
                    </textarea>
                  </Box>
                )}
              </>
            )}
            <Box mt={2}>
              <Button variant="contained" onClick={onClose}>
                Close
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Box mb={1}>
              <Typography variant="h4" component="h4">
                Batch Import
              </Typography>
            </Box>
            <Box mb={3}>
              <Typography variant="body1">
                Input each URL in a new line.
              </Typography>
            </Box>
            <TextareaAutosize
              rowsMin={25}
              rowsMax={25}
              placeholder="Paste urls here"
              style={{
                width: "100%",
              }}
              name="urls"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.urls}
            />
            <Box mt={2}>
              <Button
                variant="contained"
                onClick={onClose}
                style={{ marginRight: 20 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => formik.submitForm()}
              >
                Import
              </Button>
            </Box>
          </>
        )}
      </div>
    </Modal>
  );
};
